/// <reference path="../../js/_ResizeObserver.ts" />
import Component from '../../js/_Component';
import ScrollEnforcer from '../../js/util/_ScrollEnforcer';
import ResizeClasses from '../../js/util/_ResizeClasses';

/** How far a user must drag on an item for it to flip to another item after releasing. */
const SWIPE_CUTOFF = 0.25;

/** How long a slide stays before auto-advancing. */
const TICK_TIME = 6;

export default class HomepageBanner extends Component {
	/**
	 * The slide containing element, whose height is set based on the scaler.
	 */
	private slidesElement: HTMLElement;

	/**
	 * The timer that displays slide animation timing.
	 */
	private timerElement: SVGElement;

	/**
	 * The button for going to the previous slide.
	 */
	private controlPrev: HTMLButtonElement;

	/**
	 * The button for going to the next slide.
	 */
	private controlNext: HTMLButtonElement;

	/**
	 * The button for toggling play/pause on the animation.
	 */
	private controlToggle: HTMLButtonElement;

	/**
	 * An array containing each slide in the carousel.
	 */
	private slides: HTMLElement[] = [];

	/**
	 * The currently displayed slide.
	 */
	private currentIndex = 0;

	/**
	 * Whether or not the animation is currently playing.
	 */
	private ticking = true;

	/**
	 * Time ticked during the current animation, when it reaches TICK_TIME, the slide advances.
	 */
	private timeTicked = 0;

	/**
	 * The X position of the current drag location.
	 */
	private dragX: number = null;

	/**
	 * The Y position of the current drag location.
	 */
	private dragY: number = null;

	/**
	 * The previous X position of the drag location.
	 */
	private lastX: number = null;

	/**
	 * The previous Y position of the drag location.
	 */
	private lastY: number = null;

	/**
	 * Whether or not the most recent drag is using touch.
	 */
	private usingTouch = false;

	/**
	 * Handle for any pending pause events.
	 */
	private lastPauseTimeout = null;

	constructor(element: HTMLElement) {
		super(element);

		this.slidesElement = this.element.querySelector('.c-homepage-banner--slides');
		this.timerElement = this.element.querySelector('.c-homepage-banner--icon.m-timer');
		this.controlPrev = this.element.querySelector('.c-homepage-banner--control.m-prev');
		this.controlNext = this.element.querySelector('.c-homepage-banner--control.m-next');
		this.controlToggle = this.element.querySelector('.c-homepage-banner--control.m-play-pause');

		ScrollEnforcer.preventHorizontal(this.slidesElement);

		// Detect drag actions starting with the mouse.
		this.slidesElement.addEventListener('mousedown', (e) => {
			e.preventDefault();
			this.beginDrag(e, e.clientX, e.clientY);
			this.usingTouch = false;
		});

		// Detect drag actions starting with touch.
		this.slidesElement.addEventListener('touchstart', (e) => {
			this.beginDrag(e, e.touches[0].clientX, e.touches[0].clientY);
			this.usingTouch = true;
		});

		// Intercept clicking after a drag event to avoid opening links with the same action.
		this.slidesElement.addEventListener('click', (e) => this.handleClick(e), true);

		// Add each slide.
		Array.from(this.slidesElement.children).forEach((slide) => {
			this.slides.push(slide as HTMLElement);
			slide.classList.add('c-homepage-banner--slide');
		});

		// Add slider controls.
		this.controlPrev.addEventListener('click', () => this.prevSlide());
		this.controlNext.addEventListener('click', () => this.nextSlide());
		this.controlToggle.addEventListener('click', () => this.toggle());

		// Set mobile mode.
		new ResizeClasses(this.element, {
			'm-mobile': {
				max: 768,
			},
			'm-very-wide': {
				min: 1792,
			},
		});

		// First tick uses current time as previous time, too.
		requestAnimationFrame((t) => this.tick(t, t));
	}

	/**
	 * Go to the next slide.
	 */
	nextSlide() {
		this.timeTicked = 0;
		this.currentIndex += 1;
		this.updateTransform();
		this.updateTimer();
		this.sendPauseEvent();
		this.queueClearEvent();
	}

	/**
	 * Go to the previous slide.
	 */
	prevSlide() {
		this.timeTicked = 0;
		this.currentIndex -= 1;
		this.updateTransform();
		this.updateTimer();
		this.sendPauseEvent();
		this.queueClearEvent();
	}

	/**
	 * Update the transform for the slider based on the position.
	 */
	updateTransform() {
		// Wrap out of bounds values.
		this.currentIndex = (this.currentIndex + this.slides.length) % this.slides.length;
		this.slidesElement.style.transform = `translate(${this.currentIndex * -100}%, 0)`;
	}

	/**
	 * Toggles animation on the carousel.
	 */
	toggle() {
		this.ticking ? this.pause() : this.play();
	}

	/**
	 * Pauses animation on the carousel.
	 */
	pause() {
		this.ticking = false;
		this.element.classList.add('m-paused');
	}

	/**
	 * Resumes animation on the carousel.
	 */
	play() {
		this.ticking = true;
		this.element.classList.remove('m-paused');
	}

	/**
	 * A function that runs in a requestAnimationFrame loop, and provides timer behavior.
	 * @param prevTime The previous time according to the requestAnimationFrame callback.
	 * @param time The current time according to the requestAnimationFrame callback.
	 */
	tick(prevTime: number, time: number) {
		requestAnimationFrame((t) => this.tick(time, t));
		if (this.ticking) {
			// Time is in milliseconds, but ticking is based on seconds.
			const delta = (time - prevTime) / 1000;
			if (delta < 1.0) { // Ignore long pauses (tabbed out).
				this.timeTicked += delta;
				if (this.timeTicked > TICK_TIME) {
					this.nextSlide();
				}
			}
			this.updateTimer();
		}
	}

	/**
	 * Updates the visual display of the timer.
	 */
	updateTimer() {
		// SVG has a radius of 40.
		this.timerElement.style.strokeDashoffset = ((this.timeTicked / TICK_TIME) * Math.PI * 40)
			.toString();
	}

	/**
	 * Sends a pause signal to all inner slides, then unpauses them after the transition ends.
	 */
	private sendPauseEvent() {
		if (this.lastPauseTimeout) {
			clearTimeout(this.lastPauseTimeout);
		}
		this.slides.forEach((slide) => {
			slide.dispatchEvent(new CustomEvent('startbanneranimation'));
		});
	}

	private queueClearEvent() {
		this.lastPauseTimeout = setTimeout(() => {
			this.slides.forEach((slide) => {
				slide.dispatchEvent(new CustomEvent('stopbanneranimation'));
			});
		}, 750);
	}

	private beginDrag(_event: Event, x: number, y: number) {
		this.element.classList.remove('m-inactive');
		this.timeTicked = 0;
		this.updateTimer();
		this.sendPauseEvent();
		this.ticking = false;
		this.lastX = x;
		this.lastY = y;
		this.dragX = x;
		this.dragY = y;
		const continueDragMouse = (e: MouseEvent) => (
			this.continueDrag(e, e.clientX, e.clientY)
		);
		const continueDragTouch = (e: TouchEvent) => (
			this.continueDrag(e, e.touches[0].clientX, e.touches[0].clientY)
		);
		const endDragMouse = (e: MouseEvent) => (
			this.endDrag(e)
		);
		const endDragTouch = (e: TouchEvent) => (
			this.endDrag(e)
		);
		const clearEvents = () => {
			document.body.removeEventListener('mousemove', continueDragMouse);
			document.body.removeEventListener('touchmove', continueDragTouch);
			document.body.removeEventListener('mouseup', endDragMouse, true);
			document.body.removeEventListener('mouseleave', endDragMouse);
			document.body.removeEventListener('touchend', endDragTouch, true);
			document.body.removeEventListener('touchcancel', endDragTouch);
			this.element.removeEventListener('carouseldragend', clearEvents);
		};

		this.element.addEventListener('carouseldragend', clearEvents);
		document.body.addEventListener('mousemove', continueDragMouse);
		document.body.addEventListener('touchmove', continueDragTouch);
		document.body.addEventListener('mouseup', endDragMouse, true);
		document.body.addEventListener('mouseleave', endDragMouse);
		document.body.addEventListener('touchend', endDragTouch, true);
		document.body.addEventListener('touchcancel', endDragTouch);
	}

	private continueDrag(_e: Event, x: number, y: number) {
		this.lastX = x;
		this.lastY = y;
		this.slidesElement.style.transform = `translate(${this.currentIndex * -100}%, 0) translate(${x - this.dragX}px, 0)`;
	}

	private endDrag(e: Event) {
		this.element.classList.add('m-inactive');
		// Resume ticking if it was ticking before the drag.
		if (!this.element.classList.contains('m-paused')) {
			this.ticking = true;
		}
		this.queueClearEvent();
		const delta = this.lastX - this.dragX;
		this.element.dispatchEvent(new CustomEvent('carouseldragend'));
		this.currentIndex += this.calculateDistanceFactor(delta, SWIPE_CUTOFF);
		this.updateTransform(); // Reset transform after dragging, regardless of update.
		if (this.usingTouch) {
			(e.target as HTMLElement).click();
		}
	}

	private handleClick(e: Event) {
		// Use a larger distance tolerance for touch input.
		const distanceTolerance = this.usingTouch ? 6 : 1;

		// Distance dragged is more than tolerance (manhattan distance).
		if (
			(Math.abs(this.lastX - this.dragX) + Math.abs(this.lastY - this.dragY))
			> distanceTolerance
		) {
			e.preventDefault();
			return false;
		}

		return true;
	}

	/**
	 * Determines, given a drag distance, what the modification would be to the current item.
	 * @param distance The distance dragged, in pixels.
	 * @param cutoff The minimum distance that must be traveled, in items, for the factor to change
	 * from 0.
	 * @returns How far left (negatives) or right (positives) the current item would be moved to
	 * match the dragged distance.
	 */
	private calculateDistanceFactor(distance: number, cutoff: number): number {
		// Determine the width of a single item.
		const itemWidth = this.slidesElement.clientWidth;
		// Determine the distance dragged, in items.
		const itemDistance = distance / itemWidth;
		if (Math.abs(itemDistance) > cutoff) { // Dragged far enough to change position.
			// Note that dragging to negative direction results in a positive number, and vice versa.
			// Round away from zero (ceil if above, floor if below).
			return Math.ceil(Math.abs(itemDistance) - cutoff) * -Math.sign(itemDistance);
		}
		return 0; // Not far enough, no factor change.
	}
}

Component.register(HomepageBanner, 'HomepageBanner');
